'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import { tw } from '@/lib/utils'

import { SortOption } from './use-directory-reducer'

interface SortControlProps {
  value: SortOption
  onChange: (sort: SortOption) => void
}

export const SORT_OPTIONS: { value: SortOption; key: string; fallback: string }[] = [
  { value: SortOption.Featured, key: 'directory.sort.featured', fallback: 'Featured' },
  { value: SortOption.TopRated, key: 'directory.sort.topRated', fallback: 'Top rated' },
  { value: SortOption.PriceLow, key: 'directory.sort.priceLow', fallback: 'Lowest price' },
  { value: SortOption.PriceHigh, key: 'directory.sort.priceHigh', fallback: 'Highest price' },
  { value: SortOption.Newest, key: 'directory.sort.newest', fallback: 'Newest' },
]

// The sort options as a segmented control (the current order is never a
// mystery). Rendered from md up; on phones the same options live at the top of
// the filter sheet (SortPills) so the toolbar stays to search + Filters.
export const SortControl = ({ value, onChange }: SortControlProps) => {
  const { t } = useTranslation()

  return (
    <div
      role="group"
      aria-label={t('directory.sort.label', 'Sort')}
      className="border-navy/15 hidden items-center gap-1 rounded-xl border bg-white p-1 md:flex"
    >
      {SORT_OPTIONS.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            data-tracking="ignore"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={tw(
              'min-h-[36px] cursor-pointer rounded-lg px-3 text-[14px] font-medium whitespace-nowrap transition-colors',
              active ? 'bg-navy text-white' : 'text-navy hover:bg-navy/5'
            )}
          >
            {t(option.key, option.fallback)}
          </button>
        )
      })}
    </div>
  )
}
